import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbols } from "../GameComponents/Symbols";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { Paylines } from "../GameComponents/Paylines";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { Symbol } from "../GameComponents/Symbol";
import { Communication } from "neo-games";
import { SymbolExtended } from "./SymbolExtended";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Settings } from "./Settings";
import { BetManager } from "../Managers/BetManager";
import { Tweens } from "../Components/Effects/Tweens";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ReelExtended } from "./ReelExtended";
import { Payline } from "../GameComponents/Payline";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { IWin } from "neo-games/Engine/Communication/Game/Types/IWin";
import { DataManager } from "../Managers/DataManager";
import { GameManager } from "../Managers/GameManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { GroupTweens } from "./GroupTweens";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    protected reelsHorizontalIndexes: Array<number> = [];
    protected reelsHorizontalMask: Array<number> = [];

    private _cycleAnimationName: string = "Value";

    public waysCounterElement: ImageTextCounterElement;
    public waysCounterValue: number = 1;
    private waysCounterDefaultScale: Object = { x: 1, y: 1 };

    public static readonly ExplodeWinSymbolsFinishedTimer: string = "explodeWinSymbolsFinishedTimer";
    public static readonly FreespinsWinSymbolsTimer: string = "freespinsWinSymbolsTimer";
    public static readonly CoinStartTimer: string = "coinStartTimer";
    public static readonly CoinFinishTimer: string = "coinFinishTimer";
    public static readonly MinimumLevelUpdateTimer: string = "minimumLevelUpdateTimer";
    public static readonly ShowCustomWinTimer: string = "showCustomWinTimer";
    public static readonly ShowInitWinsTimer: string = "showInitWinsTimer";

    public static readonly MysterySymbolStartDelay: number = 20;
    public static readonly MysterySymbolAnimDelay: number = 70;

    public static readonly FinishQuickWinCalculation: string = "eventFinishQuickWinCalculation";
    public static readonly UpdateRemainingFreespinsCount: string = "eventUpdateRemainingFreespinsCount";
    public static readonly LimitWinCounter: string = "eventLimitWinCounter";

    private _teasingSymbolsArray: Object = {};

    private _runTeasingSymbolsShineTimer: FrameTimerEventEmitter;
    private _winSymbolsMoveSoundTimer: FrameTimerEventEmitter;
    private _winSymbolsShineSoundTimer: FrameTimerEventEmitter;
    private _winSymbolsExplodeSoundTimer: FrameTimerEventEmitter;
    private _collectorUpdateTimer: FrameTimerEventEmitter;
    private _fsCountUpdateTimer: FrameTimerEventEmitter;
    private _fsRemainingCountUpdateTimer: FrameTimerEventEmitter;
    private _finishFirstWinsTimer: FrameTimerEventEmitter;

    private _noTrimContainer: ContainerElement;
    private _noTrimTeasingContainer: ContainerElement;
    private _trimContainer: ContainerElement;
    private _reelTopContainer: ContainerElement;
    private _teasingBlack: SwitchElement;
    private _coinsCollector: SwitchElement;
    private _megaWaysSwitch: SwitchElement;
    private _levelSwitch: SwitchElement;
    private _multiplierSwitch: SwitchElement;
    private _groupTweens: SwitchElement;

    public currentSymbolsTrim: Array<Symbol> = [];
    public currentMysterySymbols: Array<Symbol> = [];

    protected totalWin: number;
    protected roundTotalWin: number;
    private winlineSoundNumber: number = 0;
    private _wasRetrigger: boolean = false;

    private HWOutroDelay: number = 130;

    public currentCoinWin: Communication.Game.IWin = null;

    private _freespinsCountSwitch: SwitchElement;
    private _freespinsCountValueElement: ImageTextCounterElement;
    private _freespinsCount: number = 0;

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION)) {
            this._cycleAnimationName = node.getAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION);
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.REELS_HORIZONTAL_INDEXES)) {
            this.reelsHorizontalIndexes = node
                .getAttribute(WinAnimatorExtendedAttributes.REELS_HORIZONTAL_INDEXES)
                .split(";")
                .map((value) => parseInt(value));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.REELS_HORIZONTAL_MASK)) {
            this.reelsHorizontalMask = node
                .getAttribute(WinAnimatorExtendedAttributes.REELS_HORIZONTAL_MASK)
                .split(";")
                .map((value) => parseInt(value));
        }
    }

    initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView | FreespinsView,
    ): void {
        this._runTeasingSymbolsShineTimer = CreateFrameTimer("_runTeasingSymbolsShineTimer");
        this._runTeasingSymbolsShineTimer.on(FrameTimerEvents.Complete, this.runTeasingSymbolsShineDelayed.bind(this));

        this._collectorUpdateTimer = CreateFrameTimer("_collectorUpdateTimer");
        this._collectorUpdateTimer.on(FrameTimerEvents.Complete, this.updateCoinsCollector.bind(this));

        this._fsCountUpdateTimer = CreateFrameTimer("_fsCountUpdateTimer");
        this._fsCountUpdateTimer.on(FrameTimerEvents.Complete, this.updateFreespinsCount.bind(this));

        this._finishFirstWinsTimer = CreateFrameTimer("_finishFirstWinsTimer");
        this._finishFirstWinsTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.finishFirstWins();
            }.bind(this),
        );

        this._fsRemainingCountUpdateTimer = CreateFrameTimer("_fsRemainingCountUpdateTimer");
        this._fsRemainingCountUpdateTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.emit(WinAnimatorExtended.UpdateRemainingFreespinsCount);
            }.bind(this),
        );

        this._winSymbolsMoveSoundTimer = CreateFrameTimer("_winSymbolsMoveSoundTimer");
        this._winSymbolsMoveSoundTimer.on(
            FrameTimerEvents.Complete,
            function () {
                PlayList.play("SND_BaseWinlineMove");
            }.bind(this),
        );

        this._winSymbolsShineSoundTimer = CreateFrameTimer("_winSymbolsShineSoundTimer");
        this._winSymbolsShineSoundTimer.on(
            FrameTimerEvents.Complete,
            function () {
                if (this.winlineSoundNumber < 3) {
                    this.winlineSoundNumber++;
                } else {
                    this.winlineSoundNumber = 1;
                }
                PlayList.play("SND_BaseWinlineShine0" + this.winlineSoundNumber);
            }.bind(this),
        );

        this._winSymbolsExplodeSoundTimer = CreateFrameTimer("_winSymbolsExplodeSoundTimer");
        this._winSymbolsExplodeSoundTimer.on(
            FrameTimerEvents.Complete,
            function () {
                PlayList.play("SND_BaseReelsExplode");
            }.bind(this),
        );

        // this._paylines = paylines;

        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (!this.value && this._cycleAnimationName && this.childs[this._cycleAnimationName]) {
            this.value = this.childs[this._cycleAnimationName];
            this.animationsContainer.addChild(this.value);
        }

        if (parent) {
            if (parent.childs["NoTrimContainer"] && parent.childs["NoTrimContainer"] instanceof ContainerElement) {
                this._noTrimContainer = parent.childs["NoTrimContainer"];
            }

            if (
                parent.childs["NoTrimTeasingContainer"] &&
                parent.childs["NoTrimTeasingContainer"] instanceof ContainerElement
            ) {
                this._noTrimTeasingContainer = parent.childs["NoTrimTeasingContainer"];
            }

            if (parent.childs["TeasingBlack"] && parent.childs["TeasingBlack"] instanceof SwitchElement) {
                this._teasingBlack = parent.childs["TeasingBlack"];
            }

            if (parent.childs["TrimContainer"] && parent.childs["TrimContainer"] instanceof ContainerElement) {
                this._trimContainer = parent.childs["TrimContainer"];
            }

            if (parent.childs["ReelTopContainer"] && parent.childs["ReelTopContainer"] instanceof ContainerElement) {
                this._reelTopContainer = parent.childs["ReelTopContainer"];
            }

            if (parent.childs["CoinsCollector"] && parent.childs["CoinsCollector"] instanceof SwitchElement) {
                this._coinsCollector = parent.childs["CoinsCollector"];

                let container: ContainerElement = this._coinsCollector.getChildElementByName("Con") as ContainerElement;

                if (container) {
                    let level: SwitchElement = container.getChildElementByName("Level") as SwitchElement;

                    if (level) {
                        this._levelSwitch = level;
                    }
                }
            }

            if (parent.childs["MegaWays"] && parent.childs["MegaWays"] instanceof SwitchElement) {
                this._megaWaysSwitch = parent.childs["MegaWays"];

                if (
                    this._megaWaysSwitch &&
                    this._megaWaysSwitch.getChildElementByName("WaysCounter") instanceof ImageTextCounterElement
                ) {
                    this.waysCounterElement = this._megaWaysSwitch.getChildElementByName(
                        "WaysCounter",
                    ) as ImageTextCounterElement;
                    this.waysCounterDefaultScale["x"] = this.waysCounterElement.scale.x;
                    this.waysCounterDefaultScale["y"] = this.waysCounterElement.scale.y;
                }
            }

            if (parent.childs["FreespinsCount"] && parent.childs["FreespinsCount"] instanceof SwitchElement) {
                this._freespinsCountSwitch = parent.childs["FreespinsCount"];

                if (this._freespinsCountSwitch) {
                    this._freespinsCountValueElement = this._freespinsCountSwitch.getChildElementByName(
                        "Value",
                    ) as ImageTextCounterElement;
                }
            }

            if (parent.childs["Multiplier"] && parent.childs["Multiplier"] instanceof SwitchElement) {
                this._multiplierSwitch = parent.childs["Multiplier"];
            }

            if (parent.childs["GroupTweens"] && parent.childs["GroupTweens"] instanceof SwitchElement) {
                this._groupTweens = parent.childs["GroupTweens"];
            }
        }
    }

    protected createAnimations(): void {
        this.animationsContainer = new ContainerElement();
        this.animationsContainer.name = "Animations";

        this.addChild(this.animationsContainer);

        if (this.childs["Value"]) {
            this.value = this.childs["Value"];

            this.animationsContainer.addChild(this.value);
        }

        this.animations = [];

        for (let i = 0; i < this.animationNames.length; i++) {
            if (this.childs[this.animationNames[i]]) {
                let animation = this.childs[this.animationNames[i]];
                animation.visible = false;
                animation.on("finished", this.onAnimationFinished.bind(this));
                animation.on(TimelineElement.AnimationComplete, this.onAnimationComplete.bind(this));
                animation.on("calculationFinished", this.onCalculationFinished.bind(this));
                animation.on("hideBuyFeature", this.hideBuyFeature.bind(this));
                animation.on("showBuyFeature", this.showBuyFeature.bind(this));

                this.animations.push(animation);

                this.animationsContainer.addChild(animation);
            }
        }

        this.currentAnimationIndex = null;
        this.currentAnimationFinished = true;
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected createSymbols(
        symbols: Symbols,
        noWinSymbolsContainer: ContainerElement,
        symbolsContainer: ContainerElement,
    ): void {
        this.symbols = symbols;

        if (noWinSymbolsContainer != null) {
            this.noWinSymbolsContainer = noWinSymbolsContainer;
        } else {
            this.noWinSymbolsContainer = new ContainerElement();
            this.noWinSymbolsContainer.name = "NoWinSymbols";

            this.addChild(this.noWinSymbolsContainer);
        }

        if (symbolsContainer != null) {
            this.symbolsContainer = symbolsContainer;
        } else {
            this.symbolsContainer = new ContainerElement();
            this.symbolsContainer.name = "Symbols";

            this.addChild(this.symbolsContainer);
        }

        this.currentSymbols = [];
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        let symbol = this.currentSymbols[reelIndex][symbolIndex];

        // exploded symbols are in empty state
        if (
            symbol != null &&
            (symbol.getCurrentCase() === Symbol.CaseEmpty ||
                symbol.getCurrentCase().indexOf(SymbolExtended.CaseAnimateEnd) != -1)
        ) {
            return false;
        }

        return true;
    }

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    private sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
        addQueue = [];
        this.noWinSymbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.noWinSymbolsContainer.addChild(symbol);
        });
    }

    showSymbols(reelsData: Array<Communication.Game.IReel>, isInit: boolean = false) {
        this.currentReels = reelsData;

        if (this.currentSymbols.length == 0) {
            for (let i = 0; i < this.currentReels.length; i++) {
                this.currentSymbols.push([]);

                for (let j = 0; j < this.currentReels[i].symbols.length; j++) {
                    this.currentSymbols[i].push(null);
                }
            }
        }

        for (let i = 0; i < this.currentReels.length; i++) {
            for (let j = 0; j < this.currentReels[i].symbols.length; j++) {
                this.removeCurrentSymbol(i, j);
            }
        }

        for (let i = 0; i < this.currentReels.length; i++) {
            for (let j = this.currentReels[i].symbols.length - 1; j >= 0; j--) {
                this.addCurrentSymbol(i, j, isInit);
            }
        }

        // this.sortSymbols(); // probably not needed here :-)
    }

    public recountingWaysCounter(value: number): void {
        // value: -1 visible true and set from reels, 0 visible false, >0 visible true and couting
        if (this.waysCounterElement == null) {
            return;
        }

        if (value == -1) {
            // set from reels
            if (
                this.currentReels != null &&
                this.reelsHorizontalMask &&
                this.reelsHorizontalMask.length == this.reelCount
            ) {
                let ways: number = 1;

                for (let i: number = 0; i < this.currentReels.length; i++) {
                    if (this.reelsHorizontalIndexes.indexOf(i) == -1) {
                        ways *= this.currentReels[i].symbols.length + this.reelsHorizontalMask[i];
                    }
                }

                if (ways != 1) {
                    // if has new count
                    this.waysCounterValue = ways;
                }
            }

            this.waysCounterElement.setText(this.waysCounterValue.toString());
            this.waysCounterElement.visible = true;
        } else if (value == 0) {
            // hide
            this.waysCounterValue = 1;
            this.waysCounterElement.setText(" ");
            this.waysCounterElement.visible = false;
        } else {
            // counting to value
            let from: number = this.waysCounterValue;
            this.waysCounterValue *= value;
            this.waysCounterElement.visible = true;

            let addScale: number = 0.1;
            Tweens.removeTweens(this.waysCounterElement, "scale");
            Tweens.scaleTween(this.waysCounterElement, {
                tweenValues: [
                    new PIXI.Point(this.waysCounterDefaultScale["x"], this.waysCounterDefaultScale["y"]),
                    new PIXI.Point(
                        this.waysCounterDefaultScale["x"] + addScale,
                        this.waysCounterDefaultScale["y"] + addScale,
                    ),
                    new PIXI.Point(this.waysCounterDefaultScale["x"], this.waysCounterDefaultScale["y"]),
                ],
                tweenDuration: 6,
            });

            this.waysCounterElement.start(from, this.waysCounterValue);
        }
    }

    onSpinReelRollFinished(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        // recounting ways counter after reel roll finished (not in onReelStopped because impact/hit animation can add delay to onReelStopped)
        if (reelsData && this.reelsHorizontalMask && this.reelsHorizontalMask.length == this.reelCount) {
            if (this.reelsHorizontalIndexes.indexOf(reelIndex) == -1) {
                this.recountingWaysCounter(reelsData[reelIndex].symbols.length + this.reelsHorizontalMask[reelIndex]);
            }
        }
    }

    onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        this.currentReels = reelsData;

        // return all symbols on reel index
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        // fix symbols array length (old vs new length) on reel index
        if (
            this.currentSymbols[reelIndex] &&
            this.currentSymbols[reelIndex].length != this.currentReels[reelIndex].symbols.length
        ) {
            if (this.currentSymbols[reelIndex].length < this.currentReels[reelIndex].symbols.length) {
                for (
                    let j = this.currentSymbols[reelIndex].length;
                    j < this.currentReels[reelIndex].symbols.length;
                    j++
                ) {
                    this.currentSymbols[reelIndex].push(null);
                }
            } else {
                while (this.currentSymbols[reelIndex].length > this.currentReels[reelIndex].symbols.length) {
                    this.currentSymbols[reelIndex].pop();
                }
            }
        }

        // add symbols
        for (let i = this.currentReels[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentSymbol(reelIndex, i);
        }

        // this.sortSymbols(); // probably not needed here :-)

        if (ReelExtended.Tease) {
            this.runTeasingSymbolsShine(10);
        }
    }

    onRespinReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        if (ReelExtended.Tease) {
            this.runTeasingSymbolsShine(10);
        }
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number, isInit: boolean = false): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            let symbolId: number = this.currentReels[reelIndex].symbols[symbolIndex];

            if (isInit) {
                if (
                    this.currentReels[reelIndex].transform != null &&
                    this.currentReels[reelIndex].transform[symbolIndex] != null &&
                    this.currentReels[reelIndex].transform[symbolIndex] != -1
                ) {
                    symbolId = this.currentReels[reelIndex].transform[symbolIndex];
                }
            }

            let symbolsLength: number = this.symbolCount[reelIndex];
            let dataSymbolsLength: number = this.currentReels[reelIndex].symbols.length;
            let symbolSizeY: number =
                dataSymbolsLength > 0 ? (symbolsLength * this.symbolHeight) / dataSymbolsLength : this.symbolHeight;
            let symbolCase: string = Symbol.CaseNormal;

            const symbol = this.symbols.getSymbol(symbolId - 1, symbolCase);

            let symbolCountCase: string;

            if (this.reelsHorizontalIndexes.indexOf(reelIndex) != -1) {
                symbol.position.x = this.reelX[reelIndex] + this.symbolWidth * symbolIndex - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] - symbol.offsetY;

                symbolCountCase = SymbolExtended.CaseCount + "_5";
                symbol.symbolCountOnReel = 5;
            } else {
                symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] + symbolSizeY * symbolIndex - symbol.offsetY;

                symbolCountCase = SymbolExtended.CaseCount + "_" + dataSymbolsLength;
                symbol.symbolCountOnReel = dataSymbolsLength;
            }

            this.currentSymbols[reelIndex][symbolIndex] = symbol;

            symbol.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
            symbol.goToCase(symbolCase);

            // NO TRIM SYMBOLS
            if (Settings.SymbolsNoTrim.indexOf(symbol.id + 1) != -1) {
                if (symbol.id + 1 == Settings.SymbolTeasing) {
                    this.addTeasingSymbolsShine(reelIndex, <SymbolExtended>symbol); // save symbol teasing reference to array
                    this._noTrimTeasingContainer.addChildAt(symbol, 0); // add to no trim container for teasing
                } else {
                    this._noTrimContainer.addChild(symbol); // add to no trim container
                }
            } else {
                // OTHERS SYMBOLS
                this.symbolsContainer.addChild(symbol); // add to win container
            }
        }
    }

    protected removeCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols && this.currentSymbols.length > 0) {
            if (this.currentSymbols[reelIndex][symbolIndex] !== null) {
                this.symbols.returnSymbol(this.currentSymbols[reelIndex][symbolIndex]);

                this.currentSymbols[reelIndex][symbolIndex] = null;
            }
        }
    }

    public removeAllCurrentSymbol(clear: boolean = false) {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                this.removeCurrentSymbol(i, j);
            }
        }

        if (clear) {
            this.currentSymbols = [];
        }
    }

    protected allSymbolsGoToReset() {
        super.allSymbolsGoToReset();
        // this.sortSymbols(); // probably not needed here :-)
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean, hardSet: boolean = false) {
        // super.allSymbolsGoToCase(caseType, noWin);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && (this.allowSymbolGoToCase(i, j, caseType) || hardSet)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }
            }
        }

        // this.sortSymbols(); // probably not needed here :-)
    }

    //******************************************************************************************************************
    //  WINS
    //******************************************************************************************************************

    showWins(
        wins: Array<Communication.Game.IWin>,
        win: number,
        totalWin?: number,
        roundTotalWin?: number,
        respin?: boolean,
        isInit?: boolean,
    ): boolean {
        this.currentWins = wins;
        this.currentWin = win;
        this.roundTotalWin = roundTotalWin;
        this.totalWin = ModuleManager.freespinsActive()
            ? this.roundTotalWin != null && !DataManager.GameLimit
                ? this.roundTotalWin
                : totalWin
            : totalWin;
        this.currentCoinWin = ModuleManager.freespinsActive()
            ? this.currentWins != null && this.currentWins.length > 0
                ? this.getRetriggerWin()
                : null
            : null;

        this.resetAnimations();
        this.clearTeasingSymbolsShine();

        if (
            (this.currentWins != null && this.currentWins.length > 0) ||
            (this.totalWin != null && this.totalWin > 0) ||
            (this.roundTotalWin != null && this.roundTotalWin > 0)
        ) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                // if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                if (this.totalWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }
        }

        // all respins done - start wincounter or HW
        if (
            ((this.currentWins != null && this.currentWins.length == 0) || this.currentCoinWin != null) &&
            this.totalWin != null &&
            this.totalWin > 0 &&
            respin
        ) {
            this.currentWin = this.totalWin; // fix for animations
            this.startAnimation(); // !!! include this.finishFirstWins()

            return this.getAnimationIndex() >= 4;
        } else if (this.currentWins != null && this.currentWins.length > 0) {
            // normal win - respin OR freespins win
            if (
                !this.hasRetriggerWin() &&
                !ModuleManager.freespinsActive() &&
                this.hasFreespinsWin() &&
                this.totalWin != null &&
                this.totalWin > 0
            ) {
                // for FS with previous win (respin+fs) needed first show counter and next start fs
                this.currentWin = this.totalWin; // fix for animations
                this.startAnimation(); // !!! include this.showCustomWin()

                return this.getAnimationIndex() >= 4; // skip showCustomWin
            }

            if (isInit != null && isInit == true) {
                // INIT
                if (this.hasRetriggerWin()) {
                    // skip ALL when is retrigger win and init
                    return false;
                }

                if (
                    ModuleManager.freespinsActive() &&
                    ((this.isLastFreespin() && !Settings.isRespin()) || DataManager.GameLimit)
                ) {
                    // skip on last fs OR limit
                    return false;
                }
            }

            this.showCustomWin(); // show wins

            if (!this.hasRetriggerWin()) {
                // skip for retrigger, because finish is in showCustomWin
                this.currentMultiplierIndex = 7; // 7 index is EMPTY counter
                this.startAnimation(); // !!! include this.finishFirstWins()
            }

            return false; // NOT HW
        } else {
            // check total win in case of state after freespins before collect
            if (!respin && !ModuleManager.freespinsActive()) {
                this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            }

            // check coins if not another wins (only coin without win, but retrigger is win (type custom))
            if (ModuleManager.freespinsActive() && (isInit == null || isInit == false)) {
                if (Settings.isNewCoins()) {
                    // if(!this.isLastFreespin()) {
                    this.coinSymbols();
                    // }
                }
            }

            return false;
        }
    }

    public showInitWins(
        wins: Array<Communication.Game.IWin>,
        win: number,
        totalWin?: number,
        roundTotalWin?: number,
    ): void {
        // save this for timer
        this.currentWins = wins;
        this.currentWin = win;
        this.totalWin = totalWin;
        this.roundTotalWin = roundTotalWin;

        this.startTimer(WinAnimatorExtended.ShowInitWinsTimer, 30);
    }

    protected showCustomInitWin(): void {
        // this.showCustomWin();
    }

    protected showCustomWin(): boolean {
        this.stopTimer();

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, DataManager.GameLimit ? SymbolExtended.CaseWinLimit : Symbol.CaseWin, false);
        }

        if (this.hasCashWin()) {
            this._winSymbolsMoveSoundTimer.stop();
            this._winSymbolsMoveSoundTimer.start(1);
            this._winSymbolsShineSoundTimer.stop();
            this._winSymbolsShineSoundTimer.start(18);
            this._winSymbolsExplodeSoundTimer.stop();
            this._winSymbolsExplodeSoundTimer.start(55);

            if (this._multiplierSwitch) {
                this._multiplierSwitch.goToCase("WIN");
            }

            this.startTimer(WinAnimatorExtended.ExplodeWinSymbolsFinishedTimer, DataManager.GameLimit ? 70 : 100); // duration is delay before explode + explode animation duration
        } else if (this.hasFreespinsWin()) {
            if (!ModuleManager.freespinsActive()) {
                this.startTimer(WinAnimatorExtended.FreespinsWinSymbolsTimer, 200); // duration for teasing symbol animation
            }
        } else {
            this.finishFirstWins();
        }

        return true;
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false, respin: boolean = false, runIdle: boolean = false): boolean {
        if (showLastSymbols) {
            this.allSymbolsGoToCase(Symbol.CaseNormal, false);
        }

        this.clearTeasingSymbolsShine();
        this._finishFirstWinsTimer.stop();

        // reset symbol container scale
        Tweens.removeTweens(this.symbolsContainer, "scale");
        this.symbolsContainer.scale.x = 1;
        this.symbolsContainer.scale.y = 1;

        if (this._teasingBlack && this._teasingBlack.getCurrentCase() != "EMPTY") {
            this._teasingBlack.goToCase("RESET");
        }

        if (!respin) {
            this.stopTimer();

            this._wasRetrigger = false;
            this.value.visible = false;

            if (spin) {
                this.recountingWaysCounter(0); // 0 reset and hide
                this.winlineSoundNumber = 0;
            } else if (runIdle) {
                this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            }

            let lastMultiplierIndex = this.currentMultiplierIndex;
            this.currentMultiplierIndex = -1;

            if (this.currentAnimationIndex != null) {
                if (this.currentAnimationFinished) {
                    this.resetAnimations();

                    this.totalWin = null;
                    this.roundTotalWin = null;
                    this.currentWins = null;
                    this.currentWin = null;
                    this.currentCoinWin = null;

                    return false;
                } else {
                    this.stopAnimation();

                    // HW interruption start FS
                    if (
                        lastMultiplierIndex >= 4 &&
                        !this.hasRetriggerWin() &&
                        !ModuleManager.freespinsActive() &&
                        this.hasFreespinsWin() &&
                        ModuleManager.pickEnabled() &&
                        this.totalWin != null &&
                        this.totalWin > 0
                    ) {
                        this.startTimer(WinAnimatorExtended.ShowCustomWinTimer, this.HWOutroDelay); // duration is HW outro delay

                        return lastMultiplierIndex >= 4; // return and skip reset(null) wins
                    }

                    // HW interruption in FS - check coin or retrigger (without HW interruption this is call in onAnimationComplete)
                    if (lastMultiplierIndex >= 4 && ModuleManager.freespinsActive()) {
                        // after wincounter or HW
                        PlayList.resetVolume("SND_FSLoop", 5);
                        PlayList.loop("SND_FSLoop");
                        if (Settings.isNewCoins() /*&& !this.isLastFreespin()*/ || this.hasRetriggerWin()) {
                            this.finishFirstWins();
                            this.startTimer(WinAnimatorExtended.CoinStartTimer, this.HWOutroDelay); // duration is HW outro delay

                            return lastMultiplierIndex >= 4; // return and skip reset(null) wins
                        }
                    }

                    if (
                        ModuleManager.freespinsActive() &&
                        (this.isLastFreespin() || DataManager.GameLimit) &&
                        !Settings.isNewCoins() &&
                        !this.hasRetriggerWin()
                    ) {
                        if (lastMultiplierIndex >= 4) {
                            this._finishFirstWinsTimer.start(this.HWOutroDelay); // finish when stop animation with delay HW
                        } else {
                            this._finishFirstWinsTimer.start(40); // finish when stop animation with delay
                        }
                    }

                    this.totalWin = null;
                    this.roundTotalWin = null;
                    this.currentWins = null;
                    this.currentWin = null;
                    this.currentCoinWin = null;

                    return lastMultiplierIndex >= 4; // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
                }
            } else {
                this.totalWin = null;
                this.roundTotalWin = null;
                this.currentWins = null;
                this.currentWin = null;
                this.currentCoinWin = null;

                return lastMultiplierIndex >= 4; // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
            }
        }

        return false;
    }

    protected idle(): void {
        let caseDelay: number = 0;
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const reelIndex = Math.floor(Math.random() * this.reelCount);
            const symbol =
                this.currentSymbols[reelIndex][Math.floor(Math.random() * this.currentSymbols[reelIndex].length)];
            if (symbol != null && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle, false, caseDelay);
                }
            }
            caseDelay += 3;
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    protected getIdleDelay(): number {
        return 60;
    }

    protected getIdleActiveElementsCount(): number {
        return 8;
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ExplodeWinSymbolsFinishedTimer:
                this.explodeWinSymbolsFinished();
                break;
            case WinAnimatorExtended.ShowInitWinsTimer:
                this.showWins(
                    this.currentWins,
                    this.currentWin,
                    this.totalWin,
                    this.roundTotalWin,
                    Settings.isRespin(),
                    true,
                );
                break;
            case WinAnimatorExtended.ShowCustomWinTimer:
                this.showCustomWin();
                break;
            case WinAnimatorExtended.FreespinsWinSymbolsTimer:
                // set teasing symbols
                for (let i = 0; i < this.currentWins.length; i++) {
                    this.winSymbolsGoToCase(i, SymbolExtended.CaseAnimateEnd, false);
                }

                this.finishFirstWins();
                break;
            case WinAnimatorExtended.CoinStartTimer:
                this.coinSymbols();
                break;
            case WinAnimatorExtended.CoinFinishTimer:
                if (Settings.isNewCoins() && Settings.CurrentCoinsCollectorCount != 3) {
                    this.coinSymbols();
                } else {
                    if (this.currentCoinWin == null) {
                        this.coinSymbols(true); // reset ALL COINS on end

                        if (this.isLastFreespin()) {
                            this._finishFirstWinsTimer.stop();
                            this._finishFirstWinsTimer.start(30); // TOTAL END for coins!!! delayed on last fs
                        } else {
                            this.finishFirstWins(); // TOTAL END for coins!!!
                        }
                    } else {
                        this.updateCoinsCollector(false, true); // reset collector, include call update level
                    }
                }
                break;
            case WinAnimatorExtended.MinimumLevelUpdateTimer:
                let delayAdd: number = 20; //40;

                if (this._megaWaysSwitch) {
                    if (
                        this._megaWaysSwitch.getCurrentCase() == "LEVEL_UP_" + Settings.getCustomDataLevel() ||
                        this._megaWaysSwitch.getCurrentCase() == "INIT_LEVEL_9"
                    ) {
                        delayAdd = 0;
                    }

                    if (this._megaWaysSwitch.getCurrentCase() != "INIT_LEVEL_9") {
                        // skip on MAX INIT level = no animations
                        this._megaWaysSwitch.goToCase("LEVEL_UP_" + Settings.getCustomDataLevel());
                    }

                    DataManager.updateDataElement(
                        GameData.DataFreeSpinsLevel,
                        GameManager.freespinsResponse.customData.level.toString(),
                    );
                }

                if (this._levelSwitch) {
                    if (this._levelSwitch.getCurrentCase() != "INIT_LEVEL_9") {
                        // skip on MAX INIT level = no animations
                        this._levelSwitch.goToCase("LEVEL_UP_" + Settings.getCustomDataLevel());
                    }
                }

                this._fsCountUpdateTimer.stop();
                this._fsCountUpdateTimer.start(20 + delayAdd);

                this.startTimer(WinAnimatorExtended.CoinFinishTimer, 80 + delayAdd); // call finish (include check win others coin)
                break;
            default:
                super.onTimerComplete();
        }
    }

    public updateFreespinsCount(value?: number): void {
        this._fsCountUpdateTimer.stop();

        if (value != null) {
            if (this._freespinsCountValueElement) {
                this._freespinsCount = value;
                this._freespinsCountValueElement.stop();
                this._freespinsCountValueElement.start(0, value, 0);
            }
        } else {
            if (this._freespinsCountSwitch) {
                this._freespinsCountSwitch.goToCase("WIN");
            }

            if (this._freespinsCountValueElement) {
                let fromValue: number = this._freespinsCount;
                let toValue: number = Settings.remainingFreespinsCount();

                this._freespinsCountValueElement.stop();
                this._freespinsCountValueElement.start(fromValue, toValue, null);

                // for update remaining freespins count
                this._fsRemainingCountUpdateTimer.stop();
                this._fsRemainingCountUpdateTimer.start(
                    this._freespinsCountValueElement.duration != null ? this._freespinsCountValueElement.duration : 20,
                );
            }
        }
    }

    protected onAnimationFinished(): void {
        this.currentAnimationFinished = true;

        this.emit(WinAnimator.FinishWinCalculation);
    }

    protected onAnimationComplete(): void {
        this.currentAnimationIndex = null;

        // for start fs after win counter DONE
        if (
            !this.hasRetriggerWin() &&
            !ModuleManager.freespinsActive() &&
            this.hasFreespinsWin() &&
            ModuleManager.pickEnabled() &&
            this.totalWin != null &&
            this.totalWin > 0
        ) {
            this.showCustomWin();
            return;
        }

        let index = this.getAnimationIndex();

        // after wincounter or HW
        if (ModuleManager.freespinsActive()) {
            if (Settings.isNewCoins() /*&& !this.isLastFreespin()*/ || this.hasRetriggerWin()) {
                if (index != null && index >= 4) {
                    PlayList.resetVolume("SND_FSLoop", 5);
                    PlayList.loop("SND_FSLoop");
                }
                this.startTimer(WinAnimatorExtended.CoinStartTimer, index != null && index >= 4 ? 0 : 30);
                return; // skip finishFirstWins
            } else if (!Settings.isRespin()) {
                if (index != null && index >= 4) {
                    PlayList.resetVolume("SND_FSLoop", 5);
                    PlayList.loop("SND_FSLoop");
                }
            }
        }

        if (
            !Settings.isRespin() &&
            !ModuleManager.pickEnabled() &&
            !(DataManager.GameLimit && index != null && index == 7)
        ) {
            this.finishFirstWins();
        }
    }

    protected onCalculationFinished(): void {
        this.emit(WinAnimatorExtended.FinishQuickWinCalculation);
    }

    public mysterySymbols(): void {
        PlayList.play("SND_BaseMysteryChange");
        for (let i = 0; i < this.currentReels.length; i++) {
            if (this.currentReels[i].transform) {
                for (let j = this.currentReels[i].transform.length - 1; j >= 0; j--) {
                    // needed this sort symbols
                    if (this.currentReels[i].transform[j] != -1) {
                        const symbol = this.currentSymbols[i][j];
                        const transformSymbol = this.symbols.getSymbol(
                            this.currentReels[i].transform[j] - 1,
                            Symbol.CaseNormal,
                        );

                        let symbolCountCase = SymbolExtended.CaseCount + "_" + this.currentReels[i].symbols.length;

                        if (this.reelsHorizontalIndexes.indexOf(i) != -1) {
                            symbolCountCase = SymbolExtended.CaseCount + "_5";
                        }

                        transformSymbol.goToCase(symbolCountCase); // for init size symbol, after this call basic CASE like NORMAL
                        transformSymbol.goToCase(Symbol.CaseNormal);
                        transformSymbol.position.x = symbol.position.x;
                        transformSymbol.position.y = symbol.position.y;
                        transformSymbol.symbolSizeOnReelY = symbol.symbolSizeOnReelY;
                        transformSymbol.symbolCountOnReel = symbol.symbolCountOnReel;

                        this.currentMysterySymbols.push(symbol); // save mystery symbol, return symbols after mystery done
                        this.currentSymbols[i][j] = transformSymbol; // replace mystery symbol by transform

                        symbol.goToCase(SymbolExtended.CaseMystery); // mystery animation

                        if (this.reelsHorizontalIndexes.indexOf(i) != -1) {
                            // for HORIZONTAL reel
                            if (this._reelTopContainer) {
                                this._reelTopContainer.visible = true;
                                this._reelTopContainer.addChild(transformSymbol); // back
                                this._reelTopContainer.addChild(symbol); // front
                            }
                        } else {
                            // for other reels
                            this._trimContainer.addChild(transformSymbol); // back
                            this._trimContainer.addChild(symbol); // front
                        }
                    }
                }
            }
        }
    }

    public coinSymbols(reset: boolean = false): Array<Symbol> {
        const symbols: Array<Symbol> = [];

        let newCoinsCount: number = Settings.getNewCoins();
        let index: number = 0; // coin only on reel index 0

        if (newCoinsCount > 0 || this.currentCoinWin != null || reset) {
            for (let j = 0; j < this.currentReels[index].symbols.length; j++) {
                if (this.currentReels[index].symbols[j] != null) {
                    const symbol = this.currentSymbols[index][j];

                    if (symbol.id + 1 == Settings.SymbolSpecial) {
                        if (reset) {
                            if (symbol.getCurrentCase() == Symbol.CaseWin) {
                                // only for WIN special symbol (coin)
                                symbol.goToCase(SymbolExtended.CaseCollected); // reset coin
                            }
                        } else {
                            if (
                                symbol.getCurrentCase() != Symbol.CaseWin &&
                                symbol.getCurrentCase() != SymbolExtended.CaseCollected
                            ) {
                                // only for special symbol (coin) which not already win
                                if (this._noTrimContainer) {
                                    this._noTrimContainer.addChild(symbol);
                                }

                                symbol.goToCase(Symbol.CaseWin); // coin animation
                                symbols.push(symbol);

                                PlayList.play("SND_FSCoinAnim");

                                this._collectorUpdateTimer.stop();
                                this._collectorUpdateTimer.start(90); // delay animation symbol on reel

                                break; // break because only one coin animation in time
                            }
                        }
                    }
                }
            }
        }

        return symbols;
    }

    public machineStopped(): void {
        if (this.currentMysterySymbols && this.currentMysterySymbols.length > 0) {
            while (this.currentMysterySymbols.length > 0) {
                this.symbols.returnSymbol(this.currentMysterySymbols.pop());
            }
        }
    }

    private explodeWinSymbolsFinished(): void {
        // reset symbols to EMPTY, NOT reset when is limit
        if (DataManager.GameLimit) {
            // reset symbol container scale with tween
            Tweens.removeTweens(this.symbolsContainer, "scale");
            Tweens.scaleTween(this.symbolsContainer, {
                tweenValues: [
                    new PIXI.Point(this.symbolsContainer.scale.x, this.symbolsContainer.scale.y),
                    new PIXI.Point(1, 1),
                ],
                tweenDuration: 6,
                callbackFunction: (): void => {
                    this.totalWin = ModuleManager.freespinsActive()
                        ? this.roundTotalWin != null && !DataManager.GameLimit
                            ? this.roundTotalWin
                            : this.totalWin
                        : this.totalWin;

                    // counter for LIMIT screen
                    for (let i = 0; i < this.multipliers.length; i++) {
                        if (this.totalWin / BetManager.Bet >= this.multipliers[i]) {
                            this.currentMultiplierIndex = i;
                        }
                    }

                    this.currentWin = this.totalWin; // fix for animations
                    this.startAnimation(); // // !!! include this.finishFirstWins()

                    this.emit(WinAnimatorExtended.LimitWinCounter, this.getAnimationIndex() >= 4);
                },
                callbackScope: this,
            });
        } else {
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseEmpty, false);
            }

            // reset symbol container scale
            Tweens.removeTweens(this.symbolsContainer, "scale");
            this.symbolsContainer.scale.x = 1;
            this.symbolsContainer.scale.y = 1;

            this.finishFirstWins();
        }
    }

    private hasCashWin(): boolean {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.isCashWin(this.currentWins[i])) {
                    return true;
                }
            }
        }

        return false;
    }

    private hasFreespinsWin(): boolean {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.isFreespinsWin(this.currentWins[i])) {
                    return true;
                }
            }
        }

        return false;
    }

    private hasRetriggerWin(): boolean {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.isRetriggerWin(this.currentWins[i])) {
                    return true;
                }
            }
        }

        return false;
    }

    private getRetriggerWin(): Communication.Game.IWin {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.isRetriggerWin(this.currentWins[i])) {
                    return this.currentWins[i];
                }
            }
        }

        return null;
    }

    public hasMystery(): boolean {
        let isMystery = false;
        if (this.currentReels != null) {
            for (let i = 0; i < this.currentReels.length; i++) {
                if (this.currentReels[i].transform) {
                    for (let j = 0; j < this.currentReels[i].transform.length; j++) {
                        if (this.currentReels[i].transform[j] != -1) {
                            isMystery = true;
                            break;
                        }
                    }
                }
            }
        }

        return isMystery;
    }

    private isCashWin(win: Communication.Game.IWin): boolean {
        if (win != null) {
            if (win.type == Communication.Game.WinType.Cash) {
                return true;
            }
        }

        return false;
    }

    private isFreespinsWin(win: Communication.Game.IWin): boolean {
        if (win != null) {
            if (win.type == Communication.Game.WinType.freeSpins) {
                return true;
            }
        }

        return false;
    }

    private isRetriggerWin(win: Communication.Game.IWin): boolean {
        if (win != null) {
            if (
                win.type == Communication.Game.WinType.freeSpins &&
                win.trigger == Communication.Game.WinTrigger.Custom
            ) {
                return true;
            }
        }

        return false;
    }

    protected winSymbolsGoToCaseAsWay(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        let caseSuffix: string = "";

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i] != null && this.currentSymbols[i][j] != null) {
                    if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                        const symbol = this.currentSymbols[i][j];

                        if (caseType == Symbol.CaseWin || caseType == SymbolExtended.CaseWinLimit) {
                            let count: number = (<SymbolExtended>symbol).symbolCountOnReel;

                            if (count && count > 0) {
                                caseSuffix = "_" + count;
                            }
                        }

                        symbol.goToCase(caseType + caseSuffix);

                        this.symbolsContainer.addChild(symbol);

                        symbols.push(symbol);
                    }
                }
            }
        }

        // scale whole WIN container with symbols = zoom effect
        if (symbols.length > 0) {
            if (caseType == Symbol.CaseWin || caseType == SymbolExtended.CaseWinLimit) {
                Tweens.removeTweens(this.symbolsContainer, "scale");
                Tweens.scaleTween(this.symbolsContainer, {
                    tweenValues: [new PIXI.Point(1, 1), new PIXI.Point(this.isInPortraitMode() ? 1.02 : 1.05, 1.05)],
                    tweenDuration: 6,
                });
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        let caseSuffix: string = "";

        if (caseType == Symbol.CaseWin) {
            PlayList.play("SND_BaseTeaserWin");
        }

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i] != null && this.currentSymbols[i][j] != null) {
                    if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                        const symbol = this.currentSymbols[i][j];

                        if (
                            caseType == Symbol.CaseWin ||
                            caseType == SymbolExtended.CaseWinLimit ||
                            caseType == SymbolExtended.CaseAnimateEnd
                        ) {
                            let count: number = (<SymbolExtended>symbol).symbolCountOnReel;

                            if (count && count > 0) {
                                caseSuffix = "_" + count;
                            }
                        }

                        symbol.goToCase(caseType + caseSuffix);

                        // this.symbolsContainer.addChild(symbol);
                        this._noTrimTeasingContainer.addChild(symbol);

                        symbols.push(symbol);

                        if (this._teasingBlack) {
                            this._teasingBlack.goToCase("BLACK_ALL");
                        }
                    }
                }
            }
        }

        if (symbols.length > 0) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBonusWin);
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsCustom(winIndex: number, caseType: string): Array<Symbol> {
        return this.coinSymbols();
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator, this._wasRetrigger);
    }

    public addTeasingSymbolsShine(reelIndex: number, symbol: SymbolExtended, overwrite: boolean = false): void {
        if (reelIndex && symbol) {
            if (!this._teasingSymbolsArray[reelIndex] || overwrite) {
                this._teasingSymbolsArray[reelIndex] = symbol;
            }
        }
    }

    public runTeasingSymbolsShine(delay: number = 0): void {
        if (delay > 0) {
            this._runTeasingSymbolsShineTimer.stop();
            this._runTeasingSymbolsShineTimer.start(delay);
        } else {
            this.runTeasingSymbolsShineDelayed();
        }
    }

    public stopTeasingSymbolsShine(): void {
        this._runTeasingSymbolsShineTimer.stop();

        for (let key in this._teasingSymbolsArray) {
            const symbol = this._teasingSymbolsArray[key];
            if (symbol && symbol.getCurrentCase() == SymbolExtended.CaseTeasingIn) {
                symbol.goToCase(SymbolExtended.CaseTeasingOut);
            }
        }
    }

    private runTeasingSymbolsShineDelayed(): void {
        this._runTeasingSymbolsShineTimer.stop();

        for (let key in this._teasingSymbolsArray) {
            const symbol = this._teasingSymbolsArray[key];
            if (symbol && symbol.getCurrentCase() != SymbolExtended.CaseTeasingIn) {
                symbol.goToCase(SymbolExtended.CaseTeasingIn);
            }
        }
    }

    public clearTeasingSymbolsShine(): void {
        this._runTeasingSymbolsShineTimer.stop();

        // clear teasing symbols array
        if (!Settings.isRespin()) {
            this._teasingSymbolsArray = {};
        }
    }

    public removeTrimSymbols(): void {
        while (this.currentSymbolsTrim.length > 0) {
            this.symbols.returnSymbol(this.currentSymbolsTrim.pop());
        }

        // hard reset all symbols to NORMAL
        this.allSymbolsGoToCase(Symbol.CaseNormal, false, true);
    }

    public updateCoinsCollector(isInit?: boolean, reset?: boolean): void {
        if (this._coinsCollector != null) {
            // RESET
            if (reset != null && reset == true) {
                if (Settings.CurrentCoinsCollectorCount == 3) {
                    this.currentCoinWin = null; // NEEDED remove win for finish
                    this._wasRetrigger = true;
                    this.startTimer(WinAnimatorExtended.MinimumLevelUpdateTimer, 10);
                }

                Settings.CurrentCoinsCollectorCount = 0; // update CURRENT COINS COUNT
                this._coinsCollector.goToCase("INACTIVE_DELAYED");

                if (!Settings.isNewCoins()) {
                    // not another coin after level up, can reset coins on reel
                    this.coinSymbols(true); // reset ALL COINS on end
                }

                return;
            }

            let dataCoins: number = Settings.getCustomDataCoins();

            // INIT
            if (isInit != null && isInit == true) {
                if (dataCoins > 0 && dataCoins <= 3) {
                    for (let i: number = 1; i <= dataCoins; i++) {
                        this._coinsCollector.goToCase("ACTIVE_" + i);
                    }
                } else {
                    this._coinsCollector.goToCase("INACTIVE");
                }

                if (this._megaWaysSwitch) {
                    this._megaWaysSwitch.goToCase("INIT_LEVEL_" + Settings.getCustomDataLevel()); // init update MINIMUM
                }

                if (this._levelSwitch) {
                    this._levelSwitch.goToCase("INIT_LEVEL_" + Settings.getCustomDataLevel()); // init update MINIMUM
                }

                Settings.CurrentCoinsCollectorCount = dataCoins; // update CURRENT COINS COUNT
                return;
            }

            // add ONE coin to collector
            let coinStep: number = 1;
            this._coinsCollector.goToCase("WIN_" + (Settings.CurrentCoinsCollectorCount + coinStep));
            PlayList.play("SND_FSCoin0" + (Settings.CurrentCoinsCollectorCount + coinStep));

            Settings.CurrentCoinsCollectorCount += coinStep; // update CURRENT COINS COUNT
            this.startTimer(WinAnimatorExtended.CoinFinishTimer, 60); // this call finish
        }
    }

    private isLastFreespin(): boolean {
        return (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        );
    }

    private hideBuyFeature(): void {
        ModuleManager.hideBuyFeature();
    }
    private showBuyFeature(): void {
        ModuleManager.showBuyFeature();
    }
}

export const WinAnimatorExtendedAttributes = {
    CYCLE_ANIMATION: "CycleAnimation",
    REELS_HORIZONTAL_INDEXES: "ReelsHorizontalIndexes",
    REELS_HORIZONTAL_MASK: "ReelsHorizontalMask",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/